<script lang="ts" setup>
import SkrExpansionPanel from '@/components/expansion-panel/SkrExpansionPanel.vue'

type PanelName = 'whyVerifyIdentity' | 'documentsUsed' | 'dataPrivacy' | 'howItWorks' | 'howItWorksIdent'
const props = withDefaults(
  defineProps<{
    visibleFaqs?: PanelName[]
    scrollTo?: PanelName[]
  }>(),
  {
    visibleFaqs: () => ['whyVerifyIdentity', 'documentsUsed', 'dataPrivacy', 'howItWorks'],
    scrollTo: () => [],
  }
)

const activePanel = defineModel<PanelName | null>('activePanel')
const { t } = useI18n()

const panelMap = ref<Map<PanelName, InstanceType<typeof SkrExpansionPanel>>>(new Map())

const focusPanel = (name: PanelName) => {
  panelMap.value.get(name)?.$el?.scrollIntoView({ behavior: 'smooth' })
}

watch(
  () => activePanel.value,
  newVal => {
    // Scroll to the panel if its index is in the scrollTo prop
    if (newVal && props.scrollTo.includes(newVal)) {
      focusPanel(newVal)
    }
  }
)

const setPanelRef = (name, instance) => {
  if (instance && typeof instance === 'object' && '$' in instance) {
    panelMap.value.set(name, instance)
  }
}
</script>

<template>
  <v-expansion-panels v-model="activePanel" flat class="ga-4">
    <skr-expansion-panel
      v-if="visibleFaqs.includes('whyVerifyIdentity')"
      :ref="instance => setPanelRef('whyVerifyIdentity', instance)"
      value="whyVerifyIdentity"
      :text="t('mab_direct_sign.q_and_a.question_1.answer')"
      :title="t('mab_direct_sign.q_and_a.question_1.title')"
    />
    <skr-expansion-panel
      v-if="visibleFaqs.includes('documentsUsed')"
      :ref="instance => setPanelRef('documentsUsed', instance)"
      value="documentsUsed"
      :title="t('mab_direct_sign.q_and_a.question_2.title')"
    >
      <template #text>
        <i18n-t
          keypath="mab_direct_sign.q_and_a.question_2.answer"
          tag="div"
          for="mab_direct_sign.q_and_a.question_2.answer_link_label"
        >
          <a :href="t('mab_direct_sign.q_and_a.question_2.answer_link_url')" target="_blank" rel="noopener">{{
            t('mab_direct_sign.q_and_a.question_2.answer_link_label')
          }}</a>
        </i18n-t>
      </template>
    </skr-expansion-panel>
    <skr-expansion-panel
      v-if="visibleFaqs.includes('dataPrivacy')"
      :ref="instance => setPanelRef('dataPrivacy', instance)"
      value="dataPrivacy"
      :text="t('mab_direct_sign.q_and_a.question_3.answer')"
      :title="t('mab_direct_sign.q_and_a.question_3.title')"
    />
    <skr-expansion-panel
      v-if="visibleFaqs.includes('howItWorks')"
      :ref="instance => setPanelRef('howItWorks', instance)"
      value="howItWorks"
    >
      <template #title>
        {{ t('mab_direct_sign.q_and_a.question_4.title') }}
      </template>
      <template #text>
        <div class="whitespace-pre-wrap">{{ t('mab_direct_sign.q_and_a.question_4.answer') }}</div>
      </template>
    </skr-expansion-panel>
    <skr-expansion-panel
      v-if="visibleFaqs.includes('howItWorksIdent')"
      :ref="instance => setPanelRef('howItWorksIdent', instance)"
      value="howItWorksIdent"
    >
      <template #title>
        {{ t('mab_direct_sign.q_and_a.question_4_ident.title') }}
      </template>
      <template #text>
        <div class="whitespace-pre-wrap">{{ t('mab_direct_sign.q_and_a.question_4_ident.answer') }}</div>
      </template>
    </skr-expansion-panel>
  </v-expansion-panels>
</template>
